import { useDeviceStore } from '@/stores/device';

const NO_CONNECTION_ROUTE_NAME = 'no-connection';
const OFFLINE_RETURN_PATH_KEY = 'offlineReturnPath';

function isSupportedCordovaPlatform() {
  return ['android', 'ios'].includes(import.meta.env.VITE_CORDOVA_PLATFORM);
}

function readNetworkOnlineStatus() {
  const connectionType = navigator?.connection?.type;
  const noneType = window?.Connection?.NONE;

  if (connectionType && noneType) {
    return connectionType !== noneType;
  }

  return navigator?.onLine !== false;
}

function rememberReturnPath(router) {
  const currentRoute = router?.currentRoute?.value;
  if (!currentRoute || currentRoute.name === NO_CONNECTION_ROUTE_NAME) return;
  sessionStorage.setItem(OFFLINE_RETURN_PATH_KEY, currentRoute.fullPath);
}

function goToNoConnection(router) {
  if (!router || router.currentRoute.value.name === NO_CONNECTION_ROUTE_NAME) return;
  rememberReturnPath(router);
  router.replace({ name: NO_CONNECTION_ROUTE_NAME }).catch(() => {});
}

function goBackOnline(router) {
  if (!router || router.currentRoute.value.name !== NO_CONNECTION_ROUTE_NAME) return;

  const returnPath = sessionStorage.getItem(OFFLINE_RETURN_PATH_KEY) || '/feed';
  sessionStorage.removeItem(OFFLINE_RETURN_PATH_KEY);
  router.replace(returnPath).catch(() => {
    router.replace('/feed').catch(() => {});
  });
}

function setupNetworkListeners(deviceStore, router) {
  if (!isSupportedCordovaPlatform()) return;

  deviceStore.setOnline(readNetworkOnlineStatus());
  if (!deviceStore.isOnline) {
    goToNoConnection(router);
  }

  document.addEventListener(
    'offline',
    () => {
      deviceStore.setOnline(false);
      goToNoConnection(router);
    },
    false
  );

  document.addEventListener(
    'online',
    () => {
      deviceStore.setOnline(true);
      goBackOnline(router);
    },
    false
  );
}

function setupFirebaseMessaging(deviceStore) {
  const getToken = window?.cordova?.plugins?.firebase?.messaging?.getToken;
  if (typeof getToken !== 'function') return;

  getToken()
    .then((token) => {
      if (!token || token === deviceStore.phoneToken) return;
      deviceStore.setPhoneToken(token);
    })
    .catch(() => {});
}

function setupFirebasePermission() {
  const requestPermission = window?.cordova?.plugins?.firebase?.messaging?.requestPermission;
  if (typeof requestPermission !== 'function') return;

  requestPermission({ forceShow: false }).catch(() => {
    // Ignore permission errors (already denied or not supported by platform version).
  });
}

function setupAppVersion(deviceStore) {
  const appVersionPlugin = window?.cordova?.getAppVersion;
  if (!appVersionPlugin) return;
  if (typeof appVersionPlugin.getVersionNumber !== 'function') return;

  try {
    const maybePromise = appVersionPlugin.getVersionNumber((version) => {
      deviceStore.setVersion(version);
    });
    if (maybePromise && typeof maybePromise.then === 'function') {
      maybePromise.then((version) => deviceStore.setVersion(version)).catch(() => {});
    }
  } catch {
    // Ignore plugin runtime errors.
  }

  if (typeof appVersionPlugin.getVersionCode === 'function') {
    try {
      const maybePromise = appVersionPlugin.getVersionCode((versionCode) => {
        deviceStore.setVersionCode(versionCode);
      });
      if (maybePromise && typeof maybePromise.then === 'function') {
        maybePromise.then((versionCode) => deviceStore.setVersionCode(versionCode)).catch(() => {});
      }
    } catch {
      // Ignore plugin runtime errors.
    }
  }
}

function setupAppReview() {
  const requestReview = window?.cordova?.plugins?.AppReview?.requestReview;
  if (typeof requestReview !== 'function') return;

  try {
    requestReview();
  } catch {
    // Ignore plugin runtime errors.
  }
}

function setupInAppBrowser() {
  const inAppBrowser = window?.cordova?.InAppBrowser;
  const open = inAppBrowser?.open;
  if (typeof open !== 'function') return;

  const openSystem = (url, features) => {
    if (!url) return null;
    return open.call(inAppBrowser, url, '_system', features);
  };

  inAppBrowser.open = (url, _target, features) => openSystem(url, features);
  window.open = (url, _target, features) => openSystem(url, features);
}

function setupScreenOrientation() {
  const lock = window?.screen?.orientation?.lock;
  if (typeof lock !== 'function') return;

  try {
    lock('portrait');
  } catch {
    // Ignore plugin runtime errors.
  }
}

function setupExternalLinkInterceptor() {
  const open = window?.cordova?.InAppBrowser?.open;
  if (typeof open !== 'function') return;
  if (window.__edumarkExternalLinksBound) return;
  window.__edumarkExternalLinksBound = true;

  document.addEventListener(
    'click',
    (event) => {
      const anchor = event.target?.closest?.('a[href]');
      if (!anchor) return;

      const rawHref = anchor.getAttribute('href');
      if (!rawHref || rawHref.startsWith('#')) return;

      let resolvedUrl = '';
      try {
        resolvedUrl = new URL(rawHref, window.location.href).toString();
      } catch {
        return;
      }

      const isHttp = resolvedUrl.startsWith('http://') || resolvedUrl.startsWith('https://');
      if (!isHttp) return;

      event.preventDefault();
      open(resolvedUrl, '_system');
    },
    true
  );
}

function setupInAppUpdate(onNoUpdate) {
  if (import.meta.env.VITE_CORDOVA_PLATFORM !== 'android') return false;

  const inAppUpdate = window?.cordova?.plugins?.InAppUpdate;
  if (typeof inAppUpdate?.updateImmediate !== 'function') return false;

  try {
    inAppUpdate.updateImmediate(
      (status) => {
        if (status === 'UPDATE_NOT_AVAILABLE') {
          onNoUpdate?.();
        }
      },
      () => {}
    );
    return true;
  } catch {
    // Ignore plugin runtime errors.
    return false;
  }
}

export function initCordovaRuntime({ router, pinia }) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  if (window.__edumarkCordovaInitDone) return;
  window.__edumarkCordovaInitDone = true;

  const deviceStore = useDeviceStore(pinia);
  let runtimeInitialized = false;

  const runRuntimeInit = () => {
    if (runtimeInitialized) return;
    runtimeInitialized = true;
    deviceStore.setCordovaReady(true);

    setupInAppBrowser();
    setupScreenOrientation();
    setupExternalLinkInterceptor();
    setupFirebasePermission();
    setupFirebaseMessaging(deviceStore);
    setupAppVersion(deviceStore);
    const updateCheckStarted = setupInAppUpdate(setupAppReview);
    if (!updateCheckStarted) {
      setupAppReview();
    }
    setupNetworkListeners(deviceStore, router);
  };

  if (window.__edumarkCordovaDeviceReady) {
    runRuntimeInit();
    return;
  }

  document.addEventListener('deviceready', runRuntimeInit, false);
}
